import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { bookAppointmentSchema, type BookAppointmentInput } from "@dosa/validation";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  FieldError,
  FieldLabel,
  Input,
  PageLoading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { bookAppointment, listPatientAppointments, listServices } from "../../lib/api-client";

const statusVariant = { REQUESTED: "warning", CONFIRMED: "success", IN_PROGRESS: "info", COMPLETED: "neutral", CANCELLED: "neutral", NO_SHOW: "high" } as const;

export function PatientAppointmentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: appointments, isLoading } = useQuery({
    queryKey: ["patient-appointments", patientId],
    queryFn: () => listPatientAppointments(patientId),
  });
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: listServices });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BookAppointmentInput>({ resolver: zodResolver(bookAppointmentSchema), defaultValues: { channel: "VIDEO" } });

  const mutation = useMutation({
    mutationFn: (input: BookAppointmentInput) => bookAppointment(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-appointments", patientId] });
      reset();
      setOpen(false);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.appointments.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4" /> {t("patient.appointments.bookNew")}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("patient.appointments.bookNew")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor="serviceId">{t("nav.services")}</FieldLabel>
                <Controller
                  control={control}
                  name="serviceId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={Object.fromEntries((services ?? []).map((s) => [s.id, s.name]))}
                    >
                      <SelectTrigger id="serviceId">
                        <SelectValue placeholder={t("nav.services")} />
                      </SelectTrigger>
                      <SelectContent>
                        {services?.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <FieldError>{errors.serviceId?.message}</FieldError>
              </div>
              <div>
                <FieldLabel htmlFor="scheduledAt">{t("common.date")}</FieldLabel>
                <Input id="scheduledAt" type="datetime-local" {...register("scheduledAt")} />
                <FieldError>{errors.scheduledAt?.message}</FieldError>
              </div>
              <div>
                <FieldLabel htmlFor="reasonForVisit">{t("common.notes")}</FieldLabel>
                <Input id="reasonForVisit" {...register("reasonForVisit")} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {t("common.confirm")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {!appointments || appointments.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.appointments.empty")} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {appointments.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-charcoal-800">{new Date(a.scheduledAt).toLocaleString()}</p>
                    <p className="text-sm text-charcoal-400">{a.type.replaceAll("_", " ")}</p>
                  </div>
                  <Badge variant={statusVariant[a.status]}>{a.status.replaceAll("_", " ")}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
